import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Check, Star, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aestha-hub/artifacts/58pjy83g_IMG_0746.jpeg";

export default function SelectPlan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAccessExpired } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("semiannual");

  // Check if this is a renewal or plan change
  const isRenewal = location.state?.isRenewal || false;
  const isChangePlan = location.state?.isChangePlan || false;

  useEffect(() => {
    // If user is already active and NOT expired and not renewing/changing plan, redirect to dashboard
    const accessExpired = isAccessExpired ? isAccessExpired() : false;
    
    if (user && user.is_active && !accessExpired && !isRenewal && !isChangePlan) {
      navigate("/dashboard");
      return;
    }
    fetchPlans();
  }, [user, navigate, isRenewal, isChangePlan, isAccessExpired]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/api/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Error fetching plans:", error);
      toast.error("Erro ao carregar planos");
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (!selectedPlan) {
      toast.error("Selecione um plano para continuar");
      return;
    }
    navigate("/payment", { 
      state: { 
        plan: selectedPlan, 
        period: selectedPeriod,
        from: location.state?.from 
      } 
    });
  };

  const getPrice = (plan) => {
    return selectedPeriod === "monthly" ? plan.monthly_price : plan.semiannual_price;
  };

  const getPlanIcon = (planId) => {
    switch(planId) {
      case "base": return "📚";
      case "advanced": return "💼";
      case "complete": return "✨";
      default: return "📋";
    }
  };

  const getPlanLabel = (planId) => {
    switch(planId) {
      case "base": return "Base";
      case "advanced": return "Avançado";
      case "complete": return "Completo";
      default: return planId;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-skeleton w-12 h-12 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="select-plan-page">
      {/* Header */}
      <header className="border-b py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="AESTHA" className="h-8 invert" />
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-muted-foreground"
          >
            <ArrowLeft size={16} className="mr-2" />
            Voltar
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 lg:py-12">
        {/* Title */}
        <div className="text-center mb-8 lg:mb-12">
          <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl mb-3">
            Escolha seu plano
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Selecione o plano ideal para suas necessidades e comece a usar a AESTHA hoje mesmo.
          </p>
        </div>

        {/* Period Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center border p-1 rounded-none">
            <button
              onClick={() => setSelectedPeriod("monthly")}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${
                selectedPeriod === "monthly" 
                  ? "bg-black text-white" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="period-monthly"
            >
              Mensal
            </button>
            <button
              onClick={() => setSelectedPeriod("semiannual")}
              className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors flex items-center gap-2 ${
                selectedPeriod === "semiannual" 
                  ? "bg-black text-white" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid="period-semiannual"
            >
              Semestral
              <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-none">
                ECONOMIA
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6 mb-8">
          {plans.map((plan) => {
            const isSelected = selectedPlan?.id === plan.id;
            const isComplete = plan.id === "complete";
            
            return (
              <Card
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`relative cursor-pointer transition-all p-6 ${
                  isSelected 
                    ? "ring-2 ring-black shadow-lg" 
                    : "hover:shadow-md"
                } ${isComplete && selectedPeriod === "semiannual" ? "border-2 border-black" : ""}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Recommended Badge */}
                {isComplete && selectedPeriod === "semiannual" && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-black text-white text-[10px] font-mono uppercase tracking-wider px-3 py-1">
                    Recomendado
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <span className="text-3xl mb-3 block">{getPlanIcon(plan.id)}</span>
                  <h3 className="font-serif text-xl mb-1">{getPlanLabel(plan.id)}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-serif text-3xl">${getPrice(plan).toFixed(2)}</span>
                    <span className="text-muted-foreground text-sm">
                      /{selectedPeriod === "monthly" ? "mês" : "semestre"}
                    </span>
                  </div>
                  {selectedPeriod === "semiannual" && (
                    <p className="text-green-600 text-sm mt-1">
                      Economia de ${plan.semiannual_savings.toFixed(2)}
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-green-600 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Selection Indicator */}
                <div className={`w-full py-3 text-center border-t ${
                  isSelected ? "bg-black text-white" : ""
                }`}>
                  <span className="font-mono text-xs uppercase tracking-wider">
                    {isSelected ? "✓ Selecionado" : "Selecionar"}
                  </span>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <Button 
            onClick={handleContinue}
            disabled={!selectedPlan}
            className="btn-primary h-12 px-8"
            data-testid="continue-button"
          >
            Continuar para pagamento
            <ArrowRight size={16} className="ml-2" />
          </Button>

          <p className="text-xs text-muted-foreground mt-4 max-w-md mx-auto">
            Pagamento único. Sem cobrança automática. Seu acesso será liberado após a confirmação do pagamento.
          </p>
        </div>
      </main>
    </div>
  );
}
